import { useState } from 'react'
import {
  Card, Table, Button, Space, Typography, Tag, Row, Col,
  Statistic, Modal, Form, Input, Select, Tabs, DatePicker,
  message, Popconfirm,
} from 'antd'
import { AuditOutlined, LockOutlined } from '@ant-design/icons'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import apiClient from '@/shared/api/client'
import { usePlantId } from '@/shared/hooks/usePlantId'

const { Title, Text } = Typography
const API = ''

function fmtINR(v: number) {
  return `₹${Number(v ?? 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`
}

const ELIGIBILITY_COLORS: Record<string, string> = {
  ELIGIBLE: 'green', INELIGIBLE: 'red', BLOCKED: 'volcano', PROVISIONAL: 'processing',
}

const STATUS_2B: Record<string, string> = {
  MATCHED: 'success', UNMATCHED: 'error', NOT_CHECKED: 'default', FOLLOW_UP: 'warning',
}

const SALE_TYPE_COLORS: Record<string, string> = {
  B2B: 'blue', B2C: 'default', EXPORT: 'green', SCRAP: 'orange',
}

function GSTSummaryCards({ data }: { data: Record<string, unknown> }) {
  const num = (v: unknown): number => Number(v ?? 0)
  return (
    <Row gutter={12} style={{ marginBottom: 16 }}>
      {[
        { title: 'Total Taxable', value: num(data?.total_taxable) },
        { title: 'Total CGST', value: num(data?.total_cgst) },
        { title: 'Total SGST', value: num(data?.total_sgst) },
        { title: 'Total IGST', value: num(data?.total_igst) },
        { title: 'Eligible ITC', value: num(data?.eligible_itc), color: '#52c41a' },
        { title: 'Ineligible ITC', value: num(data?.ineligible_itc), color: '#ff4d4f' },
      ].map((s, i) => (
        <Col span={4} key={i}>
          <Card size="small">
            <Statistic
              title={s.title}
              value={fmtINR(s.value)}
              valueStyle={{ fontSize: 14, color: s.color }}
            />
          </Card>
        </Col>
      ))}
    </Row>
  )
}

export default function GSTRegisterPage() {
  const { plantId, plants, setPlantId } = usePlantId()
  const qc = useQueryClient()

  const [inputPeriod, setInputPeriod] = useState<dayjs.Dayjs>(dayjs())
  const [outputPeriod, setOutputPeriod] = useState<dayjs.Dayjs>(dayjs())
  const [reconPeriod, setReconPeriod] = useState<dayjs.Dayjs>(dayjs())
  const [eligFilter, setEligFilter] = useState<string | undefined>()
  const [bsFilter, set2BFilter] = useState<string | undefined>()

  const [update2BModal, setUpdate2BModal] = // eslint-disable-next-line @typescript-eslint/no-explicit-any
  useState<any>(null)
  const [update2BForm] = Form.useForm()

  const { data: inputData, isLoading: iLoading } = useQuery({
    queryKey: ['gst-input', plantId, inputPeriod?.format('YYYY-MM')],
    queryFn: () =>
      apiClient.get(`${API}/accounting/gst/input`, {
        params: {
          plant_id: plantId,
          month: inputPeriod?.month() + 1,
          year: inputPeriod?.year(),
          eligibility: eligFilter || undefined,
          status_2b: bsFilter || undefined,
          limit: 500,
        },
      }).then(r => r.data),
    enabled: !!plantId,
  })

  const { data: outputData, isLoading: oLoading } = useQuery({
    queryKey: ['gst-output', plantId, outputPeriod?.format('YYYY-MM')],
    queryFn: () =>
      apiClient.get(`${API}/accounting/gst/output`, {
        params: {
          plant_id: plantId,
          month: outputPeriod?.month() + 1,
          year: outputPeriod?.year(),
          limit: 500,
        },
      }).then(r => r.data),
    enabled: !!plantId,
  })

  const { data: reconData } = useQuery({
    queryKey: ['gst-recon', plantId, reconPeriod?.format('YYYY-MM')],
    queryFn: () =>
      apiClient.get(`${API}/accounting/gst/reconciliation`, {
        params: {
          plant_id: plantId,
          month: reconPeriod?.month() + 1,
          year: reconPeriod?.year(),
        },
      }).then(r => r.data),
    enabled: !!plantId,
  })

  const update2BMut = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) =>
      apiClient.patch(`${API}/accounting/gst/input/${id}/2b-status`, data).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['gst-input'] })
      message.success('2B status updated')
      setUpdate2BModal(null)
      update2BForm.resetFields()
    },
    onError: (e: any) => message.error(e.response?.data?.detail || 'Failed'),
  })

  const lockReconMut = useMutation({
    mutationFn: () =>
      apiClient.post(`${API}/accounting/gst/reconciliation/lock`, {
        plant_id: plantId,
        month: reconPeriod?.month() + 1,
        year: reconPeriod?.year(),
      }).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['gst-recon'] })
      message.success('Reconciliation locked')
    },
    onError: (e: any) => message.error(e.response?.data?.detail || 'Failed'),
  })

  const inputList = Array.isArray(inputData?.entries ?? inputData) ? (inputData?.entries ?? inputData) : []
  const outputList = Array.isArray(outputData?.entries ?? outputData) ? (outputData?.entries ?? outputData) : []

  const inputCols = [
    { title: 'Invoice No', dataIndex: 'invoice_no', key: 'invoice_no', width: 140,
      render: (v: string) => <span style={{ fontFamily: 'monospace' }}>{v}</span> },
    { title: 'Date', dataIndex: 'invoice_date', key: 'invoice_date', width: 100,
      render: (v: string) => v ? dayjs(v).format('DD-MMM-YY') : '—' },
    { title: 'Vendor', dataIndex: 'vendor_name', key: 'vendor_name', width: 160, ellipsis: true },
    { title: 'Taxable', dataIndex: 'taxable_value', key: 'taxable_value', width: 110, align: 'right' as const,
      render: (v: number) => fmtINR(v) },
    { title: 'CGST', dataIndex: 'cgst', key: 'cgst', width: 90, align: 'right' as const,
      render: (v: number) => fmtINR(v) },
    { title: 'SGST', dataIndex: 'sgst', key: 'sgst', width: 90, align: 'right' as const,
      render: (v: number) => fmtINR(v) },
    { title: 'IGST', dataIndex: 'igst', key: 'igst', width: 90, align: 'right' as const,
      render: (v: number) => fmtINR(v) },
    { title: 'Total GST', dataIndex: 'total_gst', key: 'total_gst', width: 100, align: 'right' as const,
      render: (v: number) => <b>{fmtINR(v)}</b> },
    { title: 'Eligibility', dataIndex: 'eligibility', key: 'eligibility', width: 110,
      render: (v: string) => <Tag color={ELIGIBILITY_COLORS[v] ?? 'default'}>{v}</Tag> },
    { title: '2B Status', dataIndex: 'status_2b', key: 'status_2b', width: 110,
      render: (v: string) => <Tag color={STATUS_2B[v] ?? 'default'}>{v?.replace('_', ' ')}</Tag> },
    { title: 'Action', key: 'action', width: 110,
      render: (_: unknown, r: any) => (
        <Button size="small" onClick={() => { setUpdate2BModal(r); update2BForm.setFieldsValue({ status_2b: r.status_2b }) }}>
          Update 2B
        </Button>
      ) },
  ]

  const outputCols = [
    { title: 'Invoice No', dataIndex: 'invoice_no', key: 'invoice_no', width: 140,
      render: (v: string) => <span style={{ fontFamily: 'monospace' }}>{v}</span> },
    { title: 'Date', dataIndex: 'invoice_date', key: 'invoice_date', width: 100,
      render: (v: string) => v ? dayjs(v).format('DD-MMM-YY') : '—' },
    { title: 'Customer', dataIndex: 'customer_name', key: 'customer_name', width: 160, ellipsis: true },
    { title: 'Sale Type', dataIndex: 'sale_type', key: 'sale_type', width: 100,
      render: (v: string) => <Tag color={SALE_TYPE_COLORS[v] ?? 'default'}>{v}</Tag> },
    { title: 'Taxable', dataIndex: 'taxable_value', key: 'taxable_value', width: 110, align: 'right' as const,
      render: (v: number) => fmtINR(v) },
    { title: 'CGST', dataIndex: 'cgst', key: 'cgst', width: 90, align: 'right' as const,
      render: (v: number) => fmtINR(v) },
    { title: 'SGST', dataIndex: 'sgst', key: 'sgst', width: 90, align: 'right' as const,
      render: (v: number) => fmtINR(v) },
    { title: 'IGST', dataIndex: 'igst', key: 'igst', width: 90, align: 'right' as const,
      render: (v: number) => fmtINR(v) },
    { title: 'Total GST', dataIndex: 'total_gst', key: 'total_gst', width: 100, align: 'right' as const,
      render: (v: number) => <b>{fmtINR(v)}</b> },
    { title: 'IRN', dataIndex: 'irn_status', key: 'irn_status', width: 90,
      render: (v: string) => v ? <Tag color="green">{v}</Tag> : <Tag>N/A</Tag> },
    { title: 'EWB', dataIndex: 'ewb_status', key: 'ewb_status', width: 90,
      render: (v: string) => v ? <Tag color="blue">{v}</Tag> : <Tag>N/A</Tag> },
  ]

  const recon = reconData ?? {}
  const netPayable = Number(recon.output_books ?? 0) - Number(recon.input_books ?? 0)

  const reconRows = [
    {
      key: 'output',
      label: 'Output GST',
      books: recon.output_books,
      filed: recon.output_gstr1,
      available: null,
      diff_books_filed: recon.output_diff,
    },
    {
      key: 'input',
      label: 'Input GST (ITC)',
      books: recon.input_books,
      filed: recon.input_gstr3b,
      available: recon.input_2b,
      diff_books_filed: recon.input_diff,
    },
  ]

  const reconCols = [
    { title: 'Head', dataIndex: 'label', key: 'label', width: 160, render: (v: string) => <b>{v}</b> },
    { title: 'Books', dataIndex: 'books', key: 'books', width: 130, align: 'right' as const,
      render: (v: number) => v != null ? fmtINR(v) : '—' },
    { title: 'GSTR-1/3B Filed', dataIndex: 'filed', key: 'filed', width: 140, align: 'right' as const,
      render: (v: number) => v != null ? fmtINR(v) : '—' },
    { title: '2B Available', dataIndex: 'available', key: 'available', width: 130, align: 'right' as const,
      render: (v: number) => v != null ? fmtINR(v) : '—' },
    { title: 'Difference', dataIndex: 'diff_books_filed', key: 'diff_books_filed', width: 110, align: 'right' as const,
      render: (v: number) => v != null
        ? <span style={{ color: Math.abs(v) > 0.5 ? 'red' : '#52c41a', fontWeight: 600 }}>{fmtINR(v)}</span>
        : '—' },
  ]

  const tabItems = [
    {
      key: 'input',
      label: 'Input Register',
      children: (
        <>
          <Row gutter={12} style={{ marginBottom: 16 }} align="middle">
            <Col>
              <Select value={plantId} onChange={setPlantId} style={{ width: 180 }}
                options={plants.map((p: any) => ({ value: p.id, label: p.name ?? p.plant_name }))} />
            </Col>
            <Col>
              <DatePicker picker="month" value={inputPeriod} onChange={v => v && setInputPeriod(v)} />
            </Col>
            <Col>
              <Select allowClear placeholder="Eligibility" style={{ width: 140 }} value={eligFilter} onChange={setEligFilter}
                options={Object.keys(ELIGIBILITY_COLORS).map(k => ({ value: k, label: k }))} />
            </Col>
            <Col>
              <Select allowClear placeholder="2B Status" style={{ width: 140 }} value={bsFilter} onChange={set2BFilter}
                options={Object.keys(STATUS_2B).map(k => ({ value: k, label: k.replace('_', ' ') }))} />
            </Col>
          </Row>
          <GSTSummaryCards data={inputData?.summary ?? {}} />
          <Table dataSource={inputList} columns={inputCols} rowKey="id" loading={iLoading}
            size="small" pagination={{ pageSize: 50 }} scroll={{ x: 1100 }} />
        </>
      ),
    },
    {
      key: 'output',
      label: 'Output Register',
      children: (
        <>
          <Row gutter={12} style={{ marginBottom: 16 }} align="middle">
            <Col>
              <DatePicker picker="month" value={outputPeriod} onChange={v => v && setOutputPeriod(v)} />
            </Col>
          </Row>
          <GSTSummaryCards data={outputData?.summary ?? {}} />
          <Table dataSource={outputList} columns={outputCols} rowKey="id" loading={oLoading}
            size="small" pagination={{ pageSize: 50 }} scroll={{ x: 1100 }} />
        </>
      ),
    },
    {
      key: 'recon',
      label: 'Monthly Reconciliation',
      children: (
        <>
          <Row gutter={12} style={{ marginBottom: 16 }} align="middle">
            <Col>
              <DatePicker picker="month" value={reconPeriod} onChange={v => v && setReconPeriod(v)} />
            </Col>
            <Col flex="auto" style={{ textAlign: 'right' }}>
              <Popconfirm
                title="Lock Reconciliation"
                description={`Lock GST reconciliation for ${reconPeriod?.format('MMM YYYY')}? This cannot be undone.`}
                onConfirm={() => lockReconMut.mutate()}
                okText="Lock"
                okButtonProps={{ danger: true }}
              >
                <Button icon={<LockOutlined />} danger>Lock Reconciliation</Button>
              </Popconfirm>
            </Col>
          </Row>
          <Table
            dataSource={reconRows}
            columns={reconCols}
            rowKey="key"
            size="small"
            pagination={false}
            style={{ marginBottom: 16 }}
          />
          <Card size="small" style={{ background: '#f6ffed', border: '1px solid #b7eb8f' }}>
            <Row>
              <Col span={12}>
                <Text strong>Net GST Payable (Output − ITC)</Text>
              </Col>
              <Col span={12} style={{ textAlign: 'right' }}>
                <Text strong style={{ fontSize: 16, color: netPayable >= 0 ? '#ff4d4f' : '#52c41a' }}>
                  {fmtINR(netPayable)}
                </Text>
              </Col>
            </Row>
          </Card>
        </>
      ),
    },
  ]

  return (
    <div style={{ padding: 24 }}>
      <Title level={3} style={{ marginBottom: 0 }}>
        <AuditOutlined style={{ marginRight: 8, color: '#1677ff' }} />
        GST Register
      </Title>
      <Text type="secondary">Input / Output tax registers and monthly reconciliation</Text>

      <Card style={{ marginTop: 16 }}>
        <Tabs items={tabItems} />
      </Card>

      {/* Update 2B Modal */}
      <Modal
        title="Update 2B Status"
        open={!!update2BModal}
        onCancel={() => { setUpdate2BModal(null); update2BForm.resetFields() }}
        onOk={() =>
          update2BForm.validateFields().then(vals =>
            update2BMut.mutate({ id: update2BModal.id, data: vals })
          )
        }
        confirmLoading={update2BMut.isPending}
      >
        <Form form={update2BForm} layout="vertical">
          <Form.Item name="status_2b" label="2B Status" rules={[{ required: true }]}>
            <Select
              options={[
                { value: 'MATCHED', label: 'Matched' },
                { value: 'UNMATCHED', label: 'Unmatched' },
                { value: 'FOLLOW_UP', label: 'Follow Up' },
              ]}
            />
          </Form.Item>
          <Form.Item name="remarks" label="Remarks">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}
